import React from 'react';
import { C } from '../../utils/styles';
import Icon from '../Icon';

function fmtMoney(n, opts = {}) {
  const v = Math.round(Number(n) || 0);
  if (opts.kCutoff && Math.abs(v) >= 1000) {
    return '$' + (v / 1000).toFixed(v >= 1_000_000 ? 2 : 1).replace(/\.0$/, '') + 'k';
  }
  return '$' + v.toLocaleString('en-US');
}

function fmtNum(n) {
  return (Number(n) || 0).toLocaleString('en-US');
}

function fmtPct(n) {
  if (!n && n !== 0) return '—';
  return Math.round(n * 100) + '%';
}

function Card({ icon, label, value, subline, color, onClick, testId }) {
  const palettes = {
    blue: { bg: '#e8f2ff', text: C.accent },
    green: { bg: '#ecfdf5', text: C.green },
    purple: { bg: '#f5f3ff', text: C.purple },
    orange: { bg: '#fffbeb', text: C.orange },
    teal: { bg: '#ccfbf1', text: '#0d9488' },
    slate: { bg: '#f1f5f9', text: '#475569' },
  };
  const palette = palettes[color] || palettes.blue;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        textAlign: 'left',
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '18px 18px 16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.15s',
        display: 'flex', flexDirection: 'column', gap: 10,
        minHeight: 130,
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}
      onMouseEnter={(e) => {
        if (!onClick) return;
        e.currentTarget.style.boxShadow = '0 8px 18px rgba(2,6,23,0.08)';
        e.currentTarget.style.borderColor = palette.text;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        if (!onClick) return;
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.03)';
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: palette.bg, color: palette.text,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name={icon} style={{ fontSize: 16 }} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textMid, letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1.3 }}>
          {label}
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: palette.text, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.05, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.4 }}>
        {subline || '\u00A0'}
      </div>
    </button>
  );
}

export default function MetricsRow({ kpis, onCardClick }) {
  const k = kpis || {};
  return (
    <div data-testid="dashboard-metrics" style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <Card
          testId="metric-total-lives"
          icon="people-outline"
          label="Total lives"
          value={fmtNum(k.totalLives)}
          subline={k.totalClients ? `Across ${k.totalClients} client${k.totalClients !== 1 ? 's' : ''}` : ''}
          color="blue"
          onClick={() => onCardClick && onCardClick('totalLives')}
        />
        <Card
          testId="metric-eligible"
          icon="checkmark-done-outline"
          label="Eligible lives"
          value={fmtNum(k.eligible)}
          subline={k.totalLives ? `${fmtPct(k.eligibleRate)} eligibility rate` : 'Awaiting census'}
          color="teal"
          onClick={() => onCardClick && onCardClick('eligible')}
        />
        <Card
          testId="metric-fica"
          icon="trending-up-outline"
          label="Annual FICA savings"
          value={fmtMoney(k.annualFica)}
          subline="Employer-side, annualised"
          color="green"
          onClick={() => onCardClick && onCardClick('annualFica')}
        />
        <Card
          testId="metric-commission"
          icon="cash-outline"
          label="Annual commission"
          value={fmtMoney(k.annualCommission)}
          subline={`${Math.round((k.brokerPct || 0) * 100)}% of spread`}
          color="purple"
          onClick={() => onCardClick && onCardClick('annualCommission')}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <Card
          testId="metric-total-clients"
          icon="business-outline"
          label="Total clients"
          value={fmtNum(k.totalClients)}
          subline="In current view"
          color="slate"
          onClick={() => onCardClick && onCardClick('totalClients')}
        />
        <Card
          testId="metric-enrolled"
          icon="person-add-outline"
          label="Enrolled lives"
          value={fmtNum(k.enrolled)}
          subline={k.eligible ? `${fmtPct(k.enrolledRate)} of eligible` : 'No eligible employees'}
          color="blue"
          onClick={() => onCardClick && onCardClick('enrolled')}
        />
        <Card
          testId="metric-hc-revenue"
          icon="layers-outline"
          label="Annual HC revenue"
          value={fmtMoney(k.annualHcRevenue)}
          subline="From active clients"
          color="orange"
          onClick={() => onCardClick && onCardClick('annualHcRevenue')}
        />
        <Card
          testId="metric-hc-margin"
          icon="trophy-outline"
          label="HC margin"
          value={fmtMoney(k.hcMargin)}
          subline={`${Math.round((k.marginPct || 0) * 100)}% of revenue`}
          color="green"
          onClick={() => onCardClick && onCardClick('hcMargin')}
        />
      </div>
    </div>
  );
}
